<template>
  <div class="fixed inset-0 bg-black/50 z-50 flex justify-end" @click.self="$emit('close')">
    <div class="bg-white dark:bg-gray-800 w-full max-w-2xl overflow-y-auto">
      <div class="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-6 py-4 flex justify-between items-center z-10">
        <div class="flex items-center gap-3">
          <h3 class="text-lg font-semibold dark:text-white">
            {{ post.keyword }}
          </h3>
          <span :class="statusClass" class="text-xs px-2 py-0.5 rounded font-medium">{{ post.status }}</span>
          <span v-if="qa?.grade" :class="gradeClass" class="text-xs px-2 py-0.5 rounded font-bold">{{ qa.grade }} ({{ qa.score }})</span>
        </div>
        <div class="flex items-center gap-3">
          <button @click="runQa" :disabled="qaRunning" class="text-sm font-medium" :class="qaRunning ? 'text-gray-400' : 'text-primary-500 hover:text-primary-600'">
            {{ qaRunning ? 'Checking...' : (qa?.grade ? 'Re-check QA' : 'Run QA') }}
          </button>
          <button @click="$emit('edit', post)" class="btn btn-secondary text-sm">
            Edit
          </button>
          <button @click="$emit('regenerate', post)" class="text-orange-500 hover:text-orange-600 text-sm font-medium">
            Regenerate
          </button>
          <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl">
            &times;
          </button>
        </div>
      </div>

      <div class="p-6">
        <div v-if="post.image_url" class="mb-6">
          <img :src="post.image_url" class="w-full rounded-lg shadow">
        </div>
        <div v-else-if="post.status === 'generating'" class="aspect-[4/3] bg-gray-100 dark:bg-gray-900 rounded-lg flex items-center justify-center text-gray-400 mb-6">
          Generating image...
        </div>

        <div v-if="post.title" class="mb-4">
          <h4 class="text-base font-semibold dark:text-white">
            {{ post.title }}
          </h4>
        </div>

        <div class="mb-6">
          <p class="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
            {{ post.body }}
          </p>
          <span class="text-xs text-gray-400 mt-1 block">{{ post.body?.length || 0 }} / 1500 characters</span>
        </div>

        <!-- QA Scorecard -->
        <div v-if="qa?.grade" class="mb-6 border dark:border-gray-700 rounded-lg overflow-hidden">
          <div class="bg-gray-50 dark:bg-gray-900 px-4 py-2 border-b dark:border-gray-700 flex justify-between items-center">
            <span class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">QA Scorecard</span>
            <div class="flex items-center gap-2">
              <span :class="gradeClass" class="text-sm font-bold px-2 py-0.5 rounded">{{ qa.grade }}</span>
              <span class="text-sm font-semibold dark:text-gray-200">{{ qa.score }}/100</span>
            </div>
          </div>
          <div class="px-4 py-3 space-y-3">
            <!-- Automated checks -->
            <div class="space-y-1.5">
              <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                SOP Checks ({{ qa.auto_score }})
              </p>
              <div v-for="check in qa.auto_checks" :key="check.rule" class="flex items-center gap-2 text-xs">
                <span v-if="check.pass" class="text-green-500 flex-shrink-0">&#10003;</span>
                <span v-else class="text-red-500 flex-shrink-0">&#10007;</span>
                <span class="dark:text-gray-300">{{ check.rule }}</span>
                <span class="text-gray-400 ml-auto truncate max-w-[200px]">{{ check.detail }}</span>
              </div>
            </div>

            <!-- LLM review scores -->
            <div v-if="qa.llm_review?.scores && Object.keys(qa.llm_review.scores).length" class="border-t dark:border-gray-700 pt-3 space-y-1.5">
              <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Content Quality ({{ qa.llm_score }}/10 avg)
              </p>
              <div v-for="(score, criterion) in qa.llm_review.scores" :key="criterion" class="flex items-center gap-2 text-xs">
                <div class="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 flex-shrink-0">
                  <div class="bg-primary-500 rounded-full h-1.5" :style="{ width: score * 10 + '%' }" />
                </div>
                <span class="dark:text-gray-300 capitalize">{{ criterion.replace(/_/g, ' ') }}</span>
                <span class="text-gray-400 ml-auto">{{ score }}/10</span>
              </div>
            </div>

            <!-- Summary and suggestions -->
            <div v-if="qa.llm_review?.summary" class="border-t dark:border-gray-700 pt-3">
              <p class="text-xs text-gray-600 dark:text-gray-400 italic">
                {{ qa.llm_review.summary }}
              </p>
            </div>
            <div v-if="qa.llm_review?.suggestions?.length" class="space-y-1">
              <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Suggestions
              </p>
              <ul class="text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                <li v-for="(s, i) in qa.llm_review.suggestions" :key="i" class="flex gap-1.5">
                  <span class="text-amber-500 flex-shrink-0">&#8226;</span>
                  {{ s }}
                </li>
              </ul>
            </div>

            <!-- Image QA: Automated checks -->
            <div v-if="qa.image_auto_checks?.length" class="border-t dark:border-gray-700 pt-3 space-y-1.5">
              <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Image SOP Checks ({{ qa.image_auto_score }})
              </p>
              <div v-for="check in qa.image_auto_checks" :key="check.rule" class="flex items-center gap-2 text-xs">
                <span v-if="check.pass" class="text-green-500 flex-shrink-0">&#10003;</span>
                <span v-else class="text-red-500 flex-shrink-0">&#10007;</span>
                <span class="dark:text-gray-300">{{ check.rule }}</span>
                <span class="text-gray-400 ml-auto truncate max-w-[200px]">{{ check.detail }}</span>
              </div>
            </div>

            <!-- Image QA: Vision LLM scores -->
            <div v-if="qa.image_llm_review?.scores && Object.keys(qa.image_llm_review.scores).length" class="border-t dark:border-gray-700 pt-3 space-y-1.5">
              <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Image Quality ({{ qa.image_llm_score }}/10 avg)
              </p>
              <div v-for="(score, criterion) in qa.image_llm_review.scores" :key="criterion" class="flex items-center gap-2 text-xs">
                <div class="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 flex-shrink-0">
                  <div class="bg-emerald-500 rounded-full h-1.5" :style="{ width: score * 10 + '%' }" />
                </div>
                <span class="dark:text-gray-300 capitalize">{{ criterion.replace(/_/g, ' ') }}</span>
                <span class="text-gray-400 ml-auto">{{ score }}/10</span>
              </div>
            </div>

            <!-- Image QA: Summary and suggestions -->
            <div v-if="qa.image_llm_review?.summary" class="border-t dark:border-gray-700 pt-3">
              <p class="text-xs text-gray-600 dark:text-gray-400 italic">
                {{ qa.image_llm_review.summary }}
              </p>
            </div>
            <div v-if="qa.image_llm_review?.suggestions?.length" class="space-y-1">
              <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Image Suggestions
              </p>
              <ul class="text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                <li v-for="(s, i) in qa.image_llm_review.suggestions" :key="i" class="flex gap-1.5">
                  <span class="text-emerald-500 flex-shrink-0">&#8226;</span>
                  {{ s }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Generation Sources -->
        <div class="mb-6 border dark:border-gray-700 rounded-lg overflow-hidden">
          <div class="bg-gray-50 dark:bg-gray-900 px-4 py-2 border-b dark:border-gray-700">
            <span class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Generation Sources</span>
          </div>
          <div class="px-4 py-3 space-y-3 text-sm">
            <div class="flex items-start gap-2">
              <span v-if="post.website_page_id" class="mt-0.5 flex-shrink-0 w-4 h-4 text-green-500">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </span>
              <span v-else class="mt-0.5 flex-shrink-0 w-4 h-4 text-amber-500">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
              </span>
              <div>
                <span class="font-medium dark:text-gray-200">Website Page</span>
                <template v-if="post.website_page_id">
                  <span class="text-green-600 dark:text-green-400 ml-1">— matched</span>
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {{ post.website_page_title || 'Untitled' }}
                    <span v-if="post.website_page_slug" class="text-gray-400"> ({{ post.website_page_slug }})</span>
                  </p>
                </template>
                <template v-else>
                  <span class="text-amber-600 dark:text-amber-400 ml-1">— no match found</span>
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Post generated without page content context
                  </p>
                </template>
              </div>
            </div>
            <div class="flex items-start gap-2">
              <span class="mt-0.5 flex-shrink-0 w-4 h-4 text-primary-500">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              </span>
              <div>
                <span class="font-medium dark:text-gray-200">LLM Provider</span>
                <span class="text-gray-500 ml-1">— {{ llmLabel }}</span>
                <p v-if="generatedAt" class="text-xs text-gray-400 mt-0.5">
                  Generated {{ generatedAt }}
                </p>
              </div>
            </div>
            <div class="flex items-start gap-2">
              <span class="mt-0.5 flex-shrink-0 w-4 h-4 text-primary-500">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              </span>
              <div>
                <span class="font-medium dark:text-gray-200">Image</span>
                <span class="text-gray-500 ml-1">— {{ post.image_url ? imageLabel : 'not generated' }}</span>
              </div>
            </div>
            <div class="flex items-start gap-2">
              <span v-if="post.utm_url" class="mt-0.5 flex-shrink-0 w-4 h-4 text-green-500">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
              </span>
              <span v-else class="mt-0.5 flex-shrink-0 w-4 h-4 text-gray-300">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
              </span>
              <div>
                <span class="font-medium dark:text-gray-200">UTM URL</span>
                <span v-if="post.utm_url" class="text-green-600 dark:text-green-400 ml-1">— built</span>
                <span v-else class="text-gray-400 ml-1">— none (no page match)</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Approval Section -->
        <div class="mb-6 border dark:border-gray-700 rounded-lg overflow-hidden">
          <div class="bg-gray-50 dark:bg-gray-900 px-4 py-2 border-b dark:border-gray-700 flex justify-between items-center">
            <span class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Client Approval</span>
            <span v-if="post.approval_status" :class="approvalBadgeClass" class="text-xs px-2 py-0.5 rounded font-medium">
              {{ approvalStatusLabel }}
            </span>
          </div>
          <div class="px-4 py-3 space-y-3">
            <!-- No approval yet -->
            <div v-if="!post.approval_status && canSendForApproval">
              <button @click="handleSendForApproval" :disabled="sendingApproval"
                class="btn btn-secondary text-sm w-full">
                {{ sendingApproval ? 'Sending...' : 'Send for Client Approval' }}
              </button>
            </div>

            <!-- Approval exists -->
            <div v-if="post.approval_status">
              <!-- Copy link -->
              <div class="flex items-center gap-2 mb-3">
                <input :value="reviewFullUrl" readonly
                  class="flex-1 bg-gray-100 dark:bg-gray-900 border dark:border-gray-700 rounded-lg px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400">
                <button @click="copyLink" class="text-primary-500 hover:text-primary-600 text-xs font-medium whitespace-nowrap">
                  {{ copied ? 'Copied!' : 'Copy Link' }}
                </button>
              </div>

              <!-- Resubmit button -->
              <button v-if="post.approval_status === 'changes_requested'"
                @click="handleResubmit" :disabled="resubmitting"
                class="btn btn-secondary text-sm w-full mb-3">
                {{ resubmitting ? 'Resubmitting...' : 'Resubmit for Review' }}
              </button>

              <!-- Comments -->
              <div v-if="approvalComments.length" class="space-y-2 mb-3">
                <div v-for="comment in approvalComments" :key="comment.id"
                  :class="comment.author_type === 'client' ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-gray-50 dark:bg-gray-900'"
                  class="rounded-lg px-3 py-2">
                  <div class="flex justify-between items-start mb-0.5">
                    <span class="text-xs font-medium" :class="comment.author_type === 'client' ? 'text-amber-700 dark:text-amber-400' : 'text-primary-700 dark:text-primary-400'">
                      {{ comment.author_name }}
                    </span>
                    <span class="text-[10px] text-gray-400">{{ formatCommentTime(comment.created_at) }}</span>
                  </div>
                  <p class="text-xs text-gray-700 dark:text-gray-300">{{ comment.body }}</p>
                </div>
              </div>

              <!-- Add internal comment -->
              <div v-if="post.approval_status !== 'approved'" class="flex gap-2">
                <input v-model="internalComment" type="text"
                  class="flex-1 border dark:border-gray-700 dark:bg-gray-900 rounded-lg px-3 py-1.5 text-xs"
                  placeholder="Add a comment..." @keyup.enter="handleAddComment">
                <button @click="handleAddComment" :disabled="!internalComment.trim()"
                  class="text-primary-500 hover:text-primary-600 disabled:text-gray-300 text-xs font-medium">
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4 text-sm border-t dark:border-gray-700 pt-4">
          <div>
            <span class="text-gray-500 dark:text-gray-400 block text-xs uppercase tracking-wide mb-1">Post Type</span>
            <span class="dark:text-gray-200 capitalize">{{ post.post_type }}</span>
          </div>
          <div>
            <span class="text-gray-500 dark:text-gray-400 block text-xs uppercase tracking-wide mb-1">CTA</span>
            <span class="dark:text-gray-200 capitalize">{{ (post.cta_type || '').replace('_', ' ') }}</span>
          </div>
          <div>
            <span class="text-gray-500 dark:text-gray-400 block text-xs uppercase tracking-wide mb-1">Scheduled Date</span>
            <span class="dark:text-gray-200">{{ post.scheduled_date || 'Not set' }}</span>
          </div>
          <div v-if="post.event_end_date">
            <span class="text-gray-500 dark:text-gray-400 block text-xs uppercase tracking-wide mb-1">Event End Date</span>
            <span class="dark:text-gray-200">{{ post.event_end_date }}</span>
          </div>
          <div v-if="post.utm_url" class="col-span-2">
            <span class="text-gray-500 dark:text-gray-400 block text-xs uppercase tracking-wide mb-1">UTM URL</span>
            <a :href="post.utm_url" target="_blank" class="text-primary-500 hover:underline break-all text-xs">{{ post.utm_url }}</a>
          </div>
        </div>

        <div v-if="post.image_prompt" class="mt-4 border-t dark:border-gray-700 pt-4">
          <span class="text-gray-500 dark:text-gray-400 block text-xs uppercase tracking-wide mb-1">Image Prompt</span>
          <p class="text-xs text-gray-500 dark:text-gray-500 italic">
            {{ post.image_prompt }}
          </p>
        </div>

        <div v-if="post.generation_metadata?.error" class="mt-4 border-t dark:border-gray-700 pt-4">
          <span class="text-red-500 block text-xs uppercase tracking-wide mb-1">Error</span>
          <p class="text-xs text-red-600 dark:text-red-400">
            {{ post.generation_metadata.error }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { qualityCheckGbpPost, sendForApproval, getApproval, resubmitApproval, createApprovalComment } from '~/javascript/api/gbp_posts'

const props = defineProps({ post: { type: Object, default: () => null } })
const emit = defineEmits(['close', 'edit', 'regenerate', 'updated'])

const qaRunning = ref(false)
const sendingApproval = ref(false)
const resubmitting = ref(false)
const copied = ref(false)
const internalComment = ref('')
const approvalComments = ref([])

const canSendForApproval = computed(() => ['completed', 'edited'].includes(props.post.status))

const approvalStatusLabels = {
  pending: 'Pending review',
  approved: 'Approved',
  changes_requested: 'Changes requested',
  revised: 'Revised'
}
const approvalStatusLabel = computed(() => approvalStatusLabels[props.post.approval_status] || '')

const approvalBadgeClasses = {
  pending: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  changes_requested: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  revised: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
}
const approvalBadgeClass = computed(() => approvalBadgeClasses[props.post.approval_status] || '')

async function loadApprovalComments() {
  if (!props.post.approval_id) return
  try {
    const { data } = await getApproval(props.post.id)
    approvalComments.value = data.comments || []
  } catch (e) {
    // no approval exists
  }
}

async function handleSendForApproval() {
  sendingApproval.value = true
  try {
    const { data } = await sendForApproval(props.post.id)
    Object.assign(props.post, { approval_status: data.status, approval_id: data.id, review_url: data.review_url })
    approvalComments.value = data.comments || []
    emit('updated')
  } catch (e) {
    console.error('Send for approval failed:', e)
  } finally {
    sendingApproval.value = false
  }
}

async function handleResubmit() {
  resubmitting.value = true
  try {
    const { data } = await resubmitApproval(props.post.id)
    Object.assign(props.post, { approval_status: data.status })
    approvalComments.value = data.comments || []
    emit('updated')
  } catch (e) {
    console.error('Resubmit failed:', e)
  } finally {
    resubmitting.value = false
  }
}

async function handleAddComment() {
  if (!internalComment.value.trim()) return
  try {
    const { data } = await createApprovalComment(props.post.id, internalComment.value)
    approvalComments.value.push(data)
    internalComment.value = ''
  } catch (e) {
    console.error('Add comment failed:', e)
  }
}

const reviewFullUrl = computed(() => {
  if (!props.post.review_url) return ''
  return `${window.location.origin}${props.post.review_url}`
})

function copyLink() {
  navigator.clipboard.writeText(reviewFullUrl.value)
  copied.value = true
  setTimeout(() => { copied.value = false }, 2000)
}

function formatCommentTime(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
}

onMounted(() => {
  loadApprovalComments()
})

const qa = computed(() => {
  const sc = props.post.qa_scorecard
  return sc && Object.keys(sc).length > 0 ? sc : null
})

async function runQa() {
  qaRunning.value = true
  try {
    const { data } = await qualityCheckGbpPost(props.post.id)
    Object.assign(props.post, data)
    emit('updated')
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('QA check failed:', e)
  } finally {
    qaRunning.value = false
  }
}

const statusClasses = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  generating: 'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  edited: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
}
const statusClass = computed(() => statusClasses[props.post.status] || '')

const gradeClasses = {
  A: 'bg-green-100 text-green-800',
  B: 'bg-primary-100 text-primary-800',
  C: 'bg-amber-100 text-amber-800',
  D: 'bg-orange-100 text-orange-800',
  F: 'bg-red-100 text-red-800'
}
const gradeClass = computed(() => gradeClasses[qa.value?.grade] || '')

const llmProviders = { anthropic: 'Claude', openai: 'GPT-4o', gemini: 'Gemini' }
const imageProviders = { gemini_images: 'Gemini Images', banana: 'Banana' }

const llmLabel = computed(() => {
  const provider = props.post.generation_metadata?.llm_provider
  return provider ? (llmProviders[provider] || provider) : 'Unknown'
})

const imageLabel = computed(() => {
  const provider = props.post.generation_metadata?.image_provider
  return provider ? (imageProviders[provider] || provider) : 'AI Generated'
})

const generatedAt = computed(() => {
  const ts = props.post.generation_metadata?.generated_at
  if (!ts) return null
  return new Date(ts).toLocaleString()
})
</script>
